import { Box, Paper, Popper, Typography } from '@mui/material'
import type { ScaleLinear } from 'd3-scale'
import type { DateTime } from 'luxon'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import AreaGraph from '@/features/bidding/components/cascading/charts/elements/AreaGraph'
import ExclusionOverlay from '@/features/bidding/components/cascading/charts/elements/ExclusionOverlay'
import HoverGroupLabels from '@/features/bidding/components/cascading/charts/elements/HoverGroupLabels'
import type { AreaGraphElement } from '@/features/bidding/components/cascading/charts/types'
import { getMarketColor } from '@/features/bidding/constants/marketColors'
import type { GroupVolume } from '@/features/bidding/hooks/useHourlyVolumeDataByGroup'
import { useHourlyVolumeDataByGroup } from '@/features/bidding/hooks/useHourlyVolumeDataByGroup'
import { useSegmentClickDetection } from '@/features/bidding/hooks/useSegmentClickDetection'
import type { BidLevelInput } from '@/features/bidding/types/bidLadderInput'
import type { ForecastMarketPair } from '@/features/bidding/types/forecastPricing'
import { getMarketProgramNames } from '@/features/bidding/utils/forecast/marketProgramNames'

type TooltipState = {
  position: { x: number; y: number }
  hour: number
  groupVolumes: GroupVolume[]
} | null

type InteractiveHourlyVolumeChartProps = {
  bidLevelInputs: BidLevelInput[]
  selectedDeliveryDay: DateTime
  market: ForecastMarketPair
  excludedPtuGroups: Set<string>
  onTogglePtuGroup: (groupUuid: string, hour: number) => void
  height?: number
}

const DEFAULT_HEIGHT = 250

function InteractiveHourlyVolumeChart({
  bidLevelInputs,
  selectedDeliveryDay,
  market,
  excludedPtuGroups,
  onTogglePtuGroup,
  height = DEFAULT_HEIGHT,
}: Readonly<InteractiveHourlyVolumeChartProps>) {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const [tooltip, setTooltip] = useState<TooltipState>(null)
  const [exclusionAnnouncement, setExclusionAnnouncement] = useState('')

  const baseDate = useMemo(() => selectedDeliveryDay.startOf('day').toJSDate(), [selectedDeliveryDay])

  const layerStroke = useCallback(() => 'white', [])

  const { data, datetimes, layerKeys, layerFill, layerKeyToGroupUuid, groupVolumes } = useHourlyVolumeDataByGroup({
    levels: bidLevelInputs,
    market,
    baseDate,
  })

  // Map layerKey -> group code for hover labels
  const layerKeyToLabel = useMemo(() => {
    const map = new Map<string, string>()
    for (const g of groupVolumes) {
      const key = `${g.direction}_${g.uuid}`
      map.set(key, g.code)
    }
    return map
  }, [groupVolumes])

  // Build excluded segments set in the format ExclusionOverlay expects: "${layerKey}:${hourIndex}"
  const excludedSegments = useMemo(() => {
    const segments = new Set<string>()
    for (const key of excludedPtuGroups) {
      const [groupUuid, hourStr] = key.split(':')
      const hour = Number(hourStr)
      for (const layerKey of layerKeys) {
        if (layerKeyToGroupUuid.get(layerKey) === groupUuid) {
          segments.add(`${layerKey}:${hour}`)
        }
      }
    }
    return segments
  }, [excludedPtuGroups, layerKeys, layerKeyToGroupUuid])

  const { resolveClick } = useSegmentClickDetection({ data, layerKeys })

  // Store valueScale from AreaGraph's renderOverlay for use in click handler.
  // Updated via useEffect to avoid writing refs during render.
  const valueScaleRef = useRef<ScaleLinear<number, number> | null>(null)
  const latestValueScaleRef = useRef<ScaleLinear<number, number> | null>(null)
  useEffect(() => {
    valueScaleRef.current = latestValueScaleRef.current
  })

  const handleClick = useCallback(
    (hourIndex: number, relativeY: number) => {
      if (!valueScaleRef.current) return

      const result = resolveClick(hourIndex, relativeY, valueScaleRef.current)
      if (!result) return

      const groupUuid = layerKeyToGroupUuid.get(result.layerKey)
      if (!groupUuid) return

      const groupCode = groupVolumes.find((g) => g.uuid === groupUuid)?.code ?? groupUuid
      const willBeExcluded = !excludedPtuGroups.has(`${groupUuid}:${hourIndex}`)
      setExclusionAnnouncement(
        willBeExcluded ? `${groupCode} excluded from hour ${hourIndex}` : `${groupCode} included in hour ${hourIndex}`,
      )

      onTogglePtuGroup(groupUuid, hourIndex)
    },
    [resolveClick, layerKeyToGroupUuid, onTogglePtuGroup, groupVolumes, excludedPtuGroups],
  )

  const handleHover = useCallback((position: { x: number; y: number } | null, dataPoint: AreaGraphElement | null) => {
    if (!position || !dataPoint) {
      setTooltip(null)
      return
    }
    const gv = dataPoint.meta.groupVolumes
    const hour = dataPoint.meta.hour
    if (!Array.isArray(gv) || typeof hour !== 'number') {
      setTooltip(null)
      return
    }
    setTooltip({
      position,
      hour,
      groupVolumes: gv as GroupVolume[],
    })
  }, [])

  const tooltipModifiers = useMemo(
    () => [
      {
        name: 'offset' as const,
        options: {
          offset: () => {
            if (!containerRef.current || datetimes.length === 0) return [0, 0]
            const chartWidth = containerRef.current.getBoundingClientRect().width
            const barHalfWidth = ((chartWidth / datetimes.length) * (1 - 0.15)) / 2
            return [0, Math.round(barHalfWidth)]
          },
        },
      },
      { name: 'flip' as const, enabled: false },
    ],
    [datetimes.length],
  )

  const hasAssignedGroups = bidLevelInputs.some((level) => level.assignedGroups.length > 0)

  if (!hasAssignedGroups) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'grey.50',
          borderRadius: 1,
        }}
      >
        <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
          {t('bidding.bidding_cascading.charts.no_volume_data')}
        </Typography>
      </Box>
    )
  }

  const marketColor = getMarketColor(market)
  const marketLabel = getMarketProgramNames(market).displayName
  const legendHeight = 24

  // Hovered hour index for showing labels on bars
  const hoveredHourIndex = tooltip?.hour ?? null

  // Labels appear on the same side as the bar half; tooltip on the opposite side
  const isHoveredBarLeftHalf = hoveredHourIndex !== null && hoveredHourIndex < datetimes.length / 2
  const tooltipPlacement = isHoveredBarLeftHalf ? 'left' : 'right'

  return (
    <Box ref={containerRef} sx={{ width: '100%', height, px: 2, position: 'relative', overflow: 'hidden' }}>
      {/* Legend header with activation group labels */}
      <Box
        sx={{
          height: legendHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '0.75rem',
          color: 'text.secondary',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap', overflow: 'hidden' }} />
        <span>MW</span>
      </Box>

      <AreaGraph
        markerAtZero
        showGridLines
        barPadding={0.15}
        data={data}
        datetimes={datetimes}
        formatTick={(v) => `${Math.abs(v).toFixed(0)}`}
        height={`${height - legendHeight}px`}
        layerFill={layerFill}
        layerKeys={layerKeys}
        layerStroke={layerStroke}
        renderOverlay={({ timeScale, valueScale }) => {
          latestValueScaleRef.current = valueScale
          return (
            <>
              <ExclusionOverlay
                barPadding={0.15}
                data={data}
                datetimes={datetimes}
                excludedSegments={excludedSegments}
                layerKeys={layerKeys}
                timeScale={timeScale}
                valueScale={valueScale}
              />
              <HoverGroupLabels
                data={data}
                datetimes={datetimes}
                hoveredHourIndex={hoveredHourIndex}
                layerKeyToLabel={layerKeyToLabel}
                layerKeys={layerKeys}
                timeScale={timeScale}
                valueScale={valueScale}
              />
            </>
          )
        }}
        testId="interactive-hourly-volume-chart"
        onClick={handleClick}
        onHover={handleHover}
      />

      {/* Tooltip */}
      <Popper
        anchorEl={
          tooltip && containerRef.current
            ? (() => {
                const container = containerRef.current
                return {
                  getBoundingClientRect: () => {
                    const containerRect = container.getBoundingClientRect()
                    const chartCenterY = containerRect.top + legendHeight + (containerRect.height - legendHeight) / 2
                    return {
                      top: chartCenterY,
                      left: tooltip.position.x,
                      bottom: chartCenterY,
                      right: tooltip.position.x,
                      width: 0,
                      height: 0,
                      x: tooltip.position.x,
                      y: chartCenterY,
                      toJSON: () => ({}),
                    }
                  },
                }
              })()
            : null
        }
        modifiers={tooltipModifiers}
        open={tooltip !== null}
        placement={tooltipPlacement}
        sx={{ zIndex: 1500, pointerEvents: 'none' }}
      >
        {tooltip && (
          <Box role="tooltip">
            <TooltipContent
              excludedPtuGroups={excludedPtuGroups}
              groupVolumes={tooltip.groupVolumes}
              hour={tooltip.hour}
              marketColor={marketColor}
              marketLabel={marketLabel}
              selectedDeliveryDay={selectedDeliveryDay}
            />
          </Box>
        )}
      </Popper>

      {/* Screen reader announcement for exclusion toggles */}
      <Box
        aria-live="polite"
        sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}
      >
        {exclusionAnnouncement}
      </Box>
    </Box>
  )
}

type TooltipContentProps = Readonly<{
  selectedDeliveryDay: DateTime
  hour: number
  groupVolumes: GroupVolume[]
  excludedPtuGroups: Set<string>
  marketColor: string
  marketLabel: string
}>

function TooltipContent({
  selectedDeliveryDay,
  hour,
  groupVolumes,
  excludedPtuGroups,
  marketColor,
  marketLabel,
}: TooltipContentProps) {
  const { t } = useTranslation()
  const upGroups = groupVolumes.filter((g) => g.direction === 'up')
  const downGroups = groupVolumes.filter((g) => g.direction === 'down')

  const upTotal = upGroups.reduce((sum, g) => sum + (excludedPtuGroups.has(`${g.uuid}:${hour}`) ? 0 : g.capacityMW), 0)
  const downTotal = downGroups.reduce(
    (sum, g) => sum + (excludedPtuGroups.has(`${g.uuid}:${hour}`) ? 0 : g.capacityMW),
    0,
  )

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1,
        backgroundColor: 'background.paper',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 0.5,
          pb: 0.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600 }}>
          {selectedDeliveryDay.toFormat('dd MMM yyyy')} {String(hour).padStart(2, '0')}:00
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: 0.5,
              backgroundColor: marketColor,
            }}
          />
          <Typography sx={{ fontSize: '0.625rem', color: 'text.secondary' }}>{marketLabel}</Typography>
        </Box>
      </Box>

      {/* Per-group breakdown (UP first, then DOWN) */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {[...upGroups, ...downGroups].map((g) => {
          const isExcluded = excludedPtuGroups.has(`${g.uuid}:${hour}`)
          return (
            <Box
              key={`${g.uuid}-${g.direction}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                opacity: isExcluded ? 0.4 : 1,
              }}
            >
              <Typography sx={{ fontSize: '0.625rem' }}>
                {g.code}{' '}
                <Typography
                  component="span"
                  sx={{
                    fontSize: '0.5625rem',
                    color: g.direction === 'up' ? 'success.main' : 'error.main',
                  }}
                >
                  {g.direction.toUpperCase()}
                </Typography>
              </Typography>
              <Typography sx={{ fontSize: '0.625rem', fontWeight: 600, textAlign: 'right' }}>
                {isExcluded ? '0.0' : g.capacityMW.toFixed(1)} MW
              </Typography>
            </Box>
          )
        })}
      </Box>

      {/* Direction totals */}
      <Box
        sx={{
          mt: 0.5,
          pt: 0.5,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.25,
        }}
      >
        {upGroups.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
            <Typography sx={{ fontSize: '0.625rem', fontWeight: 600, color: 'success.main' }}>
              {t('bidding.bidding_cascading.charts.up_total')}
            </Typography>
            <Typography sx={{ fontSize: '0.625rem', fontWeight: 600 }}>{upTotal.toFixed(1)} MW</Typography>
          </Box>
        )}
        {downGroups.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
            <Typography sx={{ fontSize: '0.625rem', fontWeight: 600, color: 'error.main' }}>
              {t('bidding.bidding_cascading.charts.down_total')}
            </Typography>
            <Typography sx={{ fontSize: '0.625rem', fontWeight: 600 }}>{downTotal.toFixed(1)} MW</Typography>
          </Box>
        )}
      </Box>
    </Paper>
  )
}

export default InteractiveHourlyVolumeChart
